import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { isEmpty, difference, uniqBy } from 'lodash';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import BtnDelete from '../Utility/FilterAction/BtnDelete';
import EditSupportingDocuments from '../SupportingDocument/EditSupportingDocuments';
import * as action from '../../Pages/Library/ColorLibary/store/ColorLibrary.action';
import FilePreview from '../FilePreview/FilePreview';
import ConfirmationPopup from '../ModalPopup/ConfirmationPopup';
import CustomTooltip from '../Tooltip/CustomTooltip';

function ColourSupportingDocs({ libraryId, openUpload, topLoading }) {
  const { t } = useTranslation('common');
  const reducerData = useSelector((reducerState) => reducerState);
  const { ColorLibraryDetails } = reducerData;
  const { colourSupportingDocs } = ColorLibraryDetails;
  const dispatch = useDispatch();

  const [editData, setEditData] = useState(null);
  const [selectedDoc, setDoc] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showFilePreviewModal, setFilePreviewModal] = useState(false);
  const [selectedFileToPreview, setFileToPreview] = useState(null);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [selectedIdArray, setSelectedIdArray] = useState([]);
  const [data, setData] = useState([]);
  const [isSelectAll, setSelectAll] = useState(false);
  const [loader, setLoader] = useState(false);

  const handleAddDocModal = async (refresh) => {
    openUpload(true);
    if (refresh) {
      setSelectedIdArray([]);
      !isEmpty(libraryId) ? await dispatch(action.getColourSupportingDocuments({ libId: libraryId })) : await dispatch(action.getColourSupportingDocuments());
    }
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      setSelectedIdArray([]);
    } else {
      const selectedItems = [...selectedIdArray];
      if (!Array.isArray(data)) {
        for (const item of data) {
          const findItems = selectedItems.filter((val) => val === item._id);
          if (findItems.length === 0) {
            selectedItems.push(item._id);
          }
        }
        setSelectedIdArray(selectedItems);
      } else {
        const Selected = [];
        data.forEach((ele) => {
          Selected.push(ele._id);
        });
        setSelectedIdArray([...Selected]);
        setSelectAll(true);
      }
    }
    setSelectAll(!isSelectAll);
  };

  useEffect(() => {
    const init = async () => {
      !isEmpty(libraryId) && libraryId !== null && (await dispatch(action.getColourSupportingDocuments({ libId: libraryId })));
    };
    init();
  }, [libraryId, dispatch]);

  useEffect(() => {
    if (Array.isArray(data) && !isEmpty(data)) {
      const stateIds = data.map((item) => item._id);
      const selectedItems = [...selectedIdArray];
      const unSelectedItems = difference(stateIds, selectedItems);
      if (unSelectedItems.length > 0) {
        setSelectAll(false);
      } else {
        setSelectAll(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdArray]);

  const showCreatedandModified = (item) => {
    return (
      (item.createdBy?.firstName || item.modifiedBy?.firstName) && (
        <button className="btn " style={{ margin: '0' }}>
          <div className="info-container testing-action-spaceremoval">
            <span className="icon-info  f-18" />
            <div className="info-content">
              {item?.createdBy?.firstName && (
                <p className="text-left m-0">
                  <strong>{t('COLOUR_SUPPORTING_DOCUMENT.CREATED')}</strong> : {`${item.createdBy?.firstName} ${item.createdBy?.lastName} | `}
                  {moment(item.createdAt).format('DD-MMMM-YYYY, h:mm a')}
                </p>
              )}
              {item.modifiedBy?.firstName && (
                <p className="text-left m-0">
                  <strong>{t('COLOUR_SUPPORTING_DOCUMENT.MODIFIED')}</strong> : {`${item.modifiedBy?.firstName} ${item.modifiedBy?.lastName} | `}
                  {moment(item.updatedAt).format('DD-MMMM-YYYY, h:mm a')}
                </p>
              )}
            </div>
          </div>
        </button>
      )
    );
  };
  const openFilePreview = (doc) => {
    const params = {
      fileName: doc.fileName,
      mimeType: doc.mimeType,
      path: doc.filePath,
    };
    setFileToPreview(params);
    setFilePreviewModal(true);
  };

  const handleDeleteModal = async () => {
    setShowConfirmationPopup(!showConfirmationPopup);
    setDoc(null);
    setData([]);
    !isEmpty(libraryId) ? await dispatch(action.getColourSupportingDocuments({ libId: libraryId })) : await dispatch(action.getColourSupportingDocuments());
  };

  const deleteDocument = (id) => {
    setDoc(id);
    setShowConfirmationPopup(!showConfirmationPopup);
  };

  const deleteById = async () => {
    setLoader(true);
    const params = {};
    if (selectedDoc) {
      params.libIds = [selectedDoc];
    } else {
      params.libIds = [...selectedIdArray];
    }
    const res = await dispatch(action.deleteColorSupportingDocs(params, t));
    if (res) {
      const arr = uniqBy(data, '_id');
      if (selectedDoc) {
        const index = arr.findIndex((ele) => ele._id === selectedDoc);
        arr.splice(index, 1);
        setData([...arr]);
        await dispatch(action.removeColourSupportingDocument(arr));
      } else {
        selectedIdArray.forEach(async (element) => {
          const index = arr.findIndex((ele) => ele._id === element);
          arr.splice(index, 1);
          setData([...arr]);
        });
        await dispatch(action.removeColourSupportingDocument(arr));
      }
      setSelectedIdArray([]);
      setSelectAll(false);
      setShowConfirmationPopup(!showConfirmationPopup);
    }
    setLoader(false);
  };

  const editDoc = (ele) => {
    setEditData(ele);
    setEditMode(true);
  };

  const handleEditModal = async (refresh) => {
    setEditMode(!editMode);
    const res = !isEmpty(editData) && (await dispatch(action.getIndividualSupportingDocuments({ docId: editData._id })));
    res && setEditData(null);
    if (refresh) {
      !isEmpty(libraryId) ? await dispatch(action.getColourSupportingDocuments({ libId: libraryId })) : await dispatch(action.getColourSupportingDocuments());
    }
  };

  const onSelectCheckBox = async (id) => {
    let selectedItems = [...selectedIdArray];
    const findItems = selectedItems.filter((item) => item === id);
    if (findItems.length > 0) {
      selectedItems = selectedItems.filter((item) => item !== id);
      setSelectedIdArray([...selectedItems]);
    } else {
      setSelectedIdArray([...selectedItems, id]);
    }
  };

  useEffect(() => {
    if (!isEmpty(colourSupportingDocs)) {
      setData(uniqBy(colourSupportingDocs, '_id'));
    }
  }, [colourSupportingDocs]);

  const togglePreview = async () => {
    topLoading(true);
    setData([]);
    setFilePreviewModal(!showFilePreviewModal);
    const res = !isEmpty(libraryId)
      ? await dispatch(action.getColourSupportingDocuments({ libId: libraryId }))
      : await dispatch(action.getColourSupportingDocuments());
    res && topLoading(false);
  };

  const downloadDocument = (doc) => {
    const { fileName } = doc;
    const path = doc.filePath;

    fetch(path)
      .then((response) => response.blob()) // Convert response to Blob
      .then((blob) => {
        const fileObjectURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = fileObjectURL;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileObjectURL); // Clean up
      })
      .catch((error) => console.error('Download error:', error));
  };

  return (
    <>
      <div className="d-flex mt-3 mb-3 align-items-center">
        <h5 className="m-0">{t('COLOUR_SUPPORTING_DOCUMENT.SUPPORTING_DOCUMENTS')}</h5>
        <div className="d-flex ml-16 mt-1">
          <CustomTooltip tooltipText={t('COMMON_LABEL.UPLOAD')}>
            <button type="button" aria-label="upload" onClick={handleAddDocModal} className="btn p-0">
              <i className="icon-Upload-1 f-24" />
            </button>
          </CustomTooltip>
          {!isEmpty(selectedIdArray) && <BtnDelete multiDelete={() => deleteDocument(null)} isDisabled={isEmpty(selectedIdArray)} />}
        </div>
      </div>
      <div className="overRight-x">
        <Table className="table custom-table tr-white" striped bordered hover responsive>
          <thead className="light-grey text-center">
            <tr className="">
              {!isEmpty(data) && (
                <td>
                  <div className="cus-chk-block text-center">
                    <input onChange={handleSelectAll} name="check_all" id="check_all" type="checkbox" checked={isSelectAll} disabled={isEmpty(data)} />
                    <label htmlFor="check_all" className="cus-chklabel" />
                  </div>
                </td>
              )}
              <td className="device-200">{t('COLOUR_SUPPORTING_DOCUMENT.DOCUMENT_NAME')}</td>
              <td className="device-100">{t('COLOUR_SUPPORTING_DOCUMENT.SIZE')}</td>
              <td className="device-150">{t('COLOUR_SUPPORTING_DOCUMENT.ACTION')}</td>
            </tr>
          </thead>
          <tbody className="tabbody">
            {!isEmpty(data) &&
              data.map((doc, i) => (
                <tr key={doc._id}>
                  <td>
                    <div className="cus-chk-block text-center">
                      <input
                        onChange={() => onSelectCheckBox(doc._id)}
                        name={`checks_${i}`}
                        id={`checks_${i}`}
                        type="checkbox"
                        checked={selectedIdArray.filter((val) => val === doc._id).length > 0}
                      />
                      <label htmlFor={`checks_${i}`} className="cus-chklabel" />
                    </div>
                  </td>
                  <td>{doc?.fileName}</td>
                  <td>
                    {doc?.size} {doc?.unit}
                  </td>
                  <td>
                    <div className="d-flex align-items-center colour-doc-table-action">
                      <CustomTooltip placement="top" tooltipText={t('COLOUR_SUPPORTING_DOCUMENT.EDIT')}>
                        <button aria-label="edit" type="button" className="btn">
                          <span className="icon-edit" onClick={() => editDoc(doc)} />
                        </button>
                      </CustomTooltip>
                      <CustomTooltip placement="top" tooltipText={t('COLOUR_SUPPORTING_DOCUMENT.FILE_PREVIEW')}>
                        <button aria-label="preview" type="button" className="btn">
                          <span className="icon-preview-template" onClick={() => openFilePreview(doc)} />
                        </button>
                      </CustomTooltip>
                      <CustomTooltip placement="top" tooltipText={t('COLOUR_SUPPORTING_DOCUMENT.DOWNLOAD')}>
                        <button aria-label="download" className="btn" type="button">
                          <span className="icon-download" onClick={() => downloadDocument(doc)} />
                        </button>
                      </CustomTooltip>
                      <CustomTooltip placement="top" tooltipText={t('COLOUR_SUPPORTING_DOCUMENT.DELETE')}>
                        <button aria-label="delete" className="btn" type="button">
                          <span className="icon-delete" onClick={() => deleteDocument(doc._id)} />
                        </button>
                      </CustomTooltip>

                      {showCreatedandModified(doc)}
                    </div>
                  </td>
                </tr>
              ))}

            {isEmpty(data) && (
              <tr>
                <td colSpan="6" className="text-center">
                  {t('COLOUR_SUPPORTING_DOCUMENT.NO_DOCUMENT_UPLOADED')}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {editMode && <EditSupportingDocuments show={editMode} handlePopup={handleEditModal} data={editData} fromColourLibrary />}
      {showFilePreviewModal && <FilePreview onOpen={showFilePreviewModal} fileObj={selectedFileToPreview} toggle={togglePreview} />}
      {showConfirmationPopup && (
        <ConfirmationPopup
          onOpen={showConfirmationPopup}
          onConfirm={deleteById}
          loading={loader}
          title={t('COLOUR_SUPPORTING_DOCUMENT.DELETE')}
          message={t('COLOUR_SUPPORTING_DOCUMENT.ARE_YOU_SURE_DELETE')}
          toggle={handleDeleteModal}
        />
      )}
    </>
  );
}

export default ColourSupportingDocs;
